import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { fetchPayments } from "@/services/payments.service";
import { useCurrency } from "@/contexts/CurrencyContext";
import type { Payment } from "@/types";

interface Notification {
  id: string;
  title: string;
  time: string;
  type: "warning" | "info" | "danger";
}

function buildNotifications(
  payments: Payment[],
  fmt: (n: number) => string,
  t: (key: string) => string
): Notification[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const notifs: Notification[] = [];

  payments.filter((p) => p.status === "overdue").forEach((p) => {
    notifs.push({ id: `overdue-${p.id}`, title: `${p.title} ${t("notifications.overdue")}`, time: fmt(p.amount), type: "danger" });
  });

  payments.filter((p) => {
    if (p.status === "paid") return false;
    const d = new Date(p.dueDate); d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  }).forEach((p) => {
    notifs.push({ id: `today-${p.id}`, title: `${p.title} ${t("notifications.dueToday")}`, time: fmt(p.amount), type: "warning" });
  });

  payments.filter((p) => {
    if (p.status === "paid" || p.status === "overdue") return false;
    const d = new Date(p.dueDate); d.setHours(0, 0, 0, 0);
    const diff = Math.ceil((d.getTime() - today.getTime()) / 86400000);
    return diff > 0 && diff <= 3;
  }).forEach((p) => {
    const d = new Date(p.dueDate); d.setHours(0, 0, 0, 0);
    const diff = Math.ceil((d.getTime() - today.getTime()) / 86400000);
    notifs.push({ id: `soon-${p.id}`, title: `${p.title} ${t("notifications.dueSoon")} ${diff} ${t("notifications.days")}`, time: fmt(p.amount), type: "info" });
  });

  return notifs.slice(0, 10);
}

export default function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { fmt } = useCurrency();
  const { t } = useTranslation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    fetchPayments()
      .then((payments) => setNotifications(buildNotifications(payments, fmt, t)))
      .catch(() => {});
  }, [fmt, t]);

  const pageTitles: Record<string, string> = {
    "/home": "PayFlow AI",
    "/payments": t("nav.allPayments"),
    "/payments/add": t("nav.addPayment"),
    "/subscriptions": t("nav.subscriptions"),
    "/analytics": t("nav.analytics"),
    "/calendar": t("nav.calendar"),
    "/documents": t("nav.documents"),
    "/profile": t("nav.profile"),
    "/profile/settings": t("nav.settings"),
    "/profile/settings/personal": t("nav.personalInfo"),
    "/profile/settings/password": t("nav.changePassword"),
    "/profile/settings/two-factor": t("nav.twoFactor"),
    "/profile/settings/devices": t("nav.devices"),
    "/profile/reminders": t("nav.reminders"),
  };

  function getPageTitle(pathname: string): string {
    if (pageTitles[pathname]) return pageTitles[pathname];
    if (/^\/payments\/[^/]+\/edit$/.test(pathname)) return t("nav.editPayment");
    if (/^\/payments\/[^/]+$/.test(pathname)) return t("nav.paymentDetail");
    return "PayFlow AI";
  }

  const title = getPageTitle(location.pathname);
  const isHome = location.pathname === "/home";
  const hasUnread = notifications.length > 0 && !seen;

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="flex items-center justify-between px-5 h-14 max-w-[375px] mx-auto">
          <div className="flex items-center gap-2">
            {!isHome && (
              <button
                onClick={() => navigate(-1)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors -ml-1"
              >
                <i className="ri-arrow-left-line text-slate-700 text-lg"></i>
              </button>
            )}
            {isHome ? (
              <h1
                className="text-lg font-semibold text-slate-900"
                style={{ fontFamily: "'Pacifico', serif" }}
              >
                PayFlow AI
              </h1>
            ) : (
              <h1 className="text-base font-semibold text-slate-900">{title}</h1>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowNotifications((v) => !v); setSeen(true); }}
              className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 transition-colors"
            >
              <i className="ri-notification-3-line text-slate-700 text-lg"></i>
              {hasUnread && (
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
              )}
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold">
              {user?.avatar || "JD"}
            </div>
          </div>
        </div>
      </nav>

      {showNotifications && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setShowNotifications(false)}
          ></div>
          <div className="fixed top-14 right-0 z-50 w-80 bg-white rounded-2xl shadow-xl border border-slate-100 mx-4 overflow-hidden max-w-[calc(100vw-2rem)]">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900">{t("notifications.title")}</h3>
              {hasUnread && (
                <span className="text-[0.625rem] font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">
                  {notifications.length}
                </span>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <i className="ri-notification-off-line text-slate-300 text-2xl mb-2"></i>
                  <p className="text-xs text-slate-400">{t("notifications.none")}</p>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => { setShowNotifications(false); navigate("/payments"); }}
                    className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer border-b border-slate-50 last:border-0"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notif.type === "warning"
                            ? "bg-amber-100 text-amber-600"
                            : notif.type === "danger"
                            ? "bg-red-100 text-red-600"
                            : "bg-blue-100 text-blue-600"
                        }`}
                      >
                        <i
                          className={
                            notif.type === "warning"
                              ? "ri-alert-line"
                              : notif.type === "danger"
                              ? "ri-error-warning-line"
                              : "ri-information-line"
                          }
                        ></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700 leading-snug">{notif.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5 font-medium">{notif.time}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="px-4 py-2.5 border-t border-slate-100">
              <button
                onClick={() => { setShowNotifications(false); navigate("/payments"); }}
                className="text-xs text-indigo-600 font-medium hover:text-indigo-700 transition-colors"
              >
                {t("notifications.viewAll")}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
